import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PageShell } from "@/components/mai/PageShell";
import { useProduct } from "@/context/ProductContext";
import { useModuleData, ModuleLoadState } from "@/hooks/useModuleData";
import { FileText, DownloadSimple, ArrowsClockwise, CircleNotch, FloppyDisk, Image as ImageIcon } from "@phosphor-icons/react";
import { ReportV2Panel } from "@/components/mai/ReportV2Panel";
import { ReportInputReadiness } from "@/components/mai/ReportInputReadiness";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Block = ({ b }) => {
  if (b.type === "paragraph") return <p className="text-[13px] text-slate-700 leading-relaxed">{b.text}</p>;
  if (b.type === "subheading") return <p className="text-[11px] uppercase tracking-widest font-bold text-slate-500 pt-2">{b.text}</p>;
  if (b.type === "kv") return (
    <table className="w-full text-[12px] border border-slate-200">
      <tbody>
        {b.items.map(([k, v], i) => (
          <tr key={i} className="border-b border-slate-100 last:border-0">
            <td className="px-3 py-1.5 font-semibold text-slate-700 bg-slate-50 w-52 align-top">{k}</td>
            <td className="px-3 py-1.5 text-slate-600">{v}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[11px] border border-slate-200">
        <thead><tr className="bg-slate-100">
          {b.headers.map((h) => <th key={h} className="px-2.5 py-1.5 text-left font-semibold text-slate-700 border-b border-slate-200">{h}</th>)}
        </tr></thead>
        <tbody>
          {b.rows.map((row, i) => (
            <tr key={i} className="border-b border-slate-100 last:border-0 align-top">
              {row.map((v, j) => <td key={j} className="px-2.5 py-1.5 text-slate-600">{v}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default function ReportPage() {
  const { productId, product } = useProduct();
  const [reportMode, setReportMode] = useState("readiness");
  const [report, setReport] = useState(null);
  const [historyList, setHistoryList] = useState([]);
  const [manual, setManual] = useState({});
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(null);

  const API_R = `${API}/report`;
  const m = useModuleData("report", [
    { key: "readiness", url: `${API_R}/${productId}/input-readiness`, label: "Input readiness assessment" },
    { key: "report", url: `${API_R}/${productId}`, label: "Report assembly" },
    { key: "history", url: `${API_R}/${productId}/history`, label: "Report history" },
  ], [productId]);

  useEffect(() => {
    if (!m.data) return;
    if (m.data.report) {
      setReport(m.data.report);
      const mm = {};
      m.data.report.sections.forEach((s) => s.manual_fields.forEach((f) => { mm[f.key] = f.value; }));
      setManual(mm);
      setDirty(false);
    }
    if (m.data.history) setHistoryList(m.data.history);
  }, [m.data]);

  const saveManual = async () => {
    await axios.put(`${API}/report/${productId}/manual`, { fields: manual });
    toast.success("Manual inputs saved — they persist across regenerations");
    m.retry();
  };
  const regenerate = async () => {
    setBusy("regen");
    try {
      const r = await axios.post(`${API}/report/${productId}/regenerate`);
      setReport(r.data);
      toast.success(`Report regenerated — now v${r.data.version} with live data`);
      axios.get(`${API}/report/${productId}/history`).then((h) => setHistoryList(h.data));
    } finally { setBusy(null); }
  };
  const doExport = async (format) => {
    setBusy(format);
    try {
      const r = await axios.post(`${API}/report/${productId}/export`, { format });
      if (r.data.draft_limitations_included) {
        toast.warning("Draft export — a limitations section is included on the first page");
      } else {
        toast.success("Export ready — downloading");
      }
      window.open(`${process.env.REACT_APP_BACKEND_URL}${r.data.download_url}`, "_blank");
      axios.get(`${API}/report/${productId}/history`).then((h) => setHistoryList(h.data));
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || "Export failed"));
    } finally { setBusy(null); }
  };

  const Btn = ({ id, label, icon: Icon, onClick, dark }) => (
    <button data-testid={`report-${id}-btn`} onClick={onClick} disabled={busy}
      className={`text-[11px] font-semibold px-3.5 py-2.5 flex items-center gap-1.5 disabled:opacity-50 ${dark ? "bg-slate-900 text-white hover:bg-slate-800" : "border border-slate-300 text-slate-700 hover:bg-slate-50"}`}>
      {busy === id || (id === "regen" && busy === "regen") ? <CircleNotch size={13} className="animate-spin" /> : <Icon size={13} />} {label}
    </button>
  );

  return (
    <PageShell pageId="report" overline={`Deliverable Engine · ${product?.name || ""}`} title="Readiness Report"
      actions={reportMode === "classic" && report && (
        <div className="flex items-center gap-2 flex-wrap">
          <Btn id="regen" label="Regenerate" icon={ArrowsClockwise} onClick={regenerate} />
          <Btn id="pdf" label="Export PDF" icon={DownloadSimple} onClick={() => doExport("pdf")} dark />
          <Btn id="brief_pdf" label="Executive Brief" icon={DownloadSimple} onClick={() => doExport("brief_pdf")} dark />
          <Btn id="docx" label="Export Word" icon={FileText} onClick={() => doExport("docx")} dark />
          <Btn id="onepager_pdf" label="One-Pager PDF" icon={DownloadSimple} onClick={() => doExport("onepager_pdf")} />
          <Btn id="onepager_png" label="One-Pager PNG" icon={ImageIcon} onClick={() => doExport("onepager_png")} />
        </div>
      )}>
      <div className="flex items-center gap-1 border-b border-slate-200 -mt-2">
        {[["readiness", "Input Readiness"], ["v2", "Three-part Readiness Report (v2)"], ["classic", "Classic Full Report"]].map(([id, label]) => (
          <button key={id} data-testid={`report-mode-${id}`} onClick={() => setReportMode(id)}
            className={`text-xs font-semibold px-4 py-2.5 border-b-2 -mb-px transition-colors ${reportMode === id ? "border-slate-900 text-slate-900" : "border-transparent text-slate-400 hover:text-slate-600"}`}>
            {label}
          </button>
        ))}
      </div>

      {reportMode === "readiness" ? (
        m.loading || m.error || !m.data?.readiness
          ? <ModuleLoadState {...m} />
          : <ReportInputReadiness data={m.data.readiness} m={m} onProceed={() => setReportMode("v2")} />
      ) : reportMode === "v2" ? <ReportV2Panel /> : (
      !report ? (
        <ModuleLoadState {...m} />
      ) : (
        <>
          {dirty && (
            <div className="sticky top-20 z-10 bg-amber-50 border border-amber-300 px-4 py-2.5 flex items-center justify-between">
              <p className="text-xs text-amber-800">Unsaved manual inputs — they will be included in every export once saved.</p>
              <button data-testid="report-save-manual-btn" onClick={saveManual}
                className="bg-slate-900 text-white text-[11px] font-semibold px-4 py-2 flex items-center gap-1.5"><FloppyDisk size={12} /> Save inputs</button>
            </div>
          )}
          <div data-testid="report-document" className="bg-white border border-slate-300 shadow-sm max-w-4xl mx-auto">
            <div className="px-10 py-12 text-center border-b-4" style={{ borderColor: "#0F2A3D" }}>
              <p className="overline-label text-slate-400">MAI Matrix™ · Global Market Access Intelligence Platform</p>
              <h2 className="font-heading font-extrabold text-3xl tracking-tight mt-3" style={{ color: "#0F2A3D" }}>{report.title}</h2>
              <p className="text-base font-semibold text-slate-700 mt-2">{report.product.name}</p>
              <p className="text-xs text-slate-500">{report.product.indication}</p>
              <p className="font-heading font-bold text-emerald-700 mt-3">GMARI™ {report.gmari.score}/100 — {report.gmari.band}</p>
              <p data-testid="report-meta" className="text-[11px] text-slate-400 mt-4">
                Prepared by {report.prepared_by} · Version {report.version} · Report as of {new Date(report.as_of).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" })}
              </p>
            </div>
            <div className="px-10 py-8 space-y-8">
              {report.sections.map((s) => (
                <section key={s.id} data-testid={`report-section-${s.id}`}>
                  <div className="flex items-baseline justify-between border-b border-slate-200 pb-1.5 mb-3">
                    <h3 className="font-heading font-bold text-sm" style={{ color: "#0F2A3D" }}>{s.title}</h3>
                    <span className={`text-[9px] uppercase tracking-widest font-bold ${s.source === "auto" ? "text-emerald-600" : s.source === "manual" ? "text-amber-600" : "text-blue-600"}`}>
                      {s.source === "auto" ? "Auto-generated" : s.source === "manual" ? "Needs manual input" : "Auto + manual"}
                    </span>
                  </div>
                  <div className="space-y-3">
                    {s.blocks.map((b, i) => <Block key={i} b={b} />)}
                    {s.manual_fields.map((f) => (
                      <div key={f.key} className="border border-dashed border-amber-300 bg-amber-50/40 p-3">
                        <p className="text-[10px] uppercase tracking-widest font-bold text-amber-700">{f.label} — manual input</p>
                        <textarea data-testid={`manual-${f.key}`} className="w-full text-xs mt-1.5 p-2 border border-slate-200 min-h-[64px] bg-white"
                          placeholder={f.placeholder} value={manual[f.key] || ""}
                          onChange={(e) => { setManual({ ...manual, [f.key]: e.target.value }); setDirty(true); }} />
                      </div>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </div>
          <section data-testid="report-history" className="max-w-4xl mx-auto bg-white border border-slate-200">
            <p className="overline-label text-slate-400 px-5 pt-4">Report history</p>
            <div className="divide-y divide-slate-100 mt-2">
              {historyList.length === 0 && <p className="text-xs text-slate-400 px-5 py-4">No exports yet.</p>}
              {historyList.map((h) => (
                <div key={h.id} className="px-5 py-2.5 flex items-center gap-3 text-xs">
                  <span className="font-mono-data text-slate-400">v{h.version}</span>
                  <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 border border-slate-300 text-slate-500">{h.format}</span>
                  <span className="flex-1 text-slate-600 truncate">{h.filename}</span>
                  <span className="text-slate-400">{new Date(h.created_at).toLocaleString()}</span>
                  <a data-testid={`history-download-${h.id}`} href={`${process.env.REACT_APP_BACKEND_URL}/api/report/files/${h.filename}`}
                    target="_blank" rel="noreferrer" className="text-emerald-700 font-semibold hover:underline">Download</a>
                </div>
              ))}
            </div>
          </section>
        </>
      ))}
    </PageShell>
  );
}
